import { RotateCcw } from 'lucide-react';
import { useCallback, useMemo } from 'react';
import { PreviewTable } from '../../../../components/PreviewTable';
import type { DataColumn } from '../../../../components/data-table/DataTable';
import { DataTablePanel } from '../../../../components/data-table/DataTablePanel';
import { useDataTable, type DataTableQuery } from '../../../../components/data-table/useDataTable';
import { StatusText } from '../../../../components/table/StatusText';
import { StackedCell } from '../../../../components/table/StackedCell';
import { TextCell } from '../../../../components/table/TextCell';
import { formatDateOnly } from '../../../../lib/formatDate';
import { formatMoney } from '../../../../lib/formatMoney';
import { PPA_EXPENSE_CLASS_LABEL, type PpaLineItemDto } from '../ppaTypes';

/**
 * A PPA's budgeted lines, on its profile — all under the PPA's one expense class (its fund
 * source, from 06 Oct 2026).
 *
 * **A `<DataTablePanel>`, not a `<PreviewGrid>`**, and that reverses what shipped for an
 * hour on 21 Sep 2026. A preview grid is READ-ONLY by its own contract — "no search, no
 * sort, no paging, no selection, no kebab, because a preview writes nothing" — and this
 * table writes: a line is corrected, deleted and restored from it. Putting Edit and Delete
 * buttons into a preview's cells gave up every one of those for nothing, and hand-rolled
 * two icon buttons where `<RowActionMenu>` already draws a permission-filtered menu with a
 * right-click.
 *
 * Three things came back with the shell, and each was a defect while it was gone: the `#`
 * gutter NUMBERS the rows (the grid derived a position from `sortOrder / 10`, which reads
 * 1, 3, 4 the moment a line is deleted — the stored sort value is never a position);
 * `<TextCell mono>` and `<DateCell>` put the dates and money back in the mono face; and a
 * deleted line arrives carrying a marker rather than silently.
 *
 * **The total sits BELOW the table, as a Table Display View** — the SALN folder's shape: a
 * read-only fact being shown rather than a value being asked for, under the rows it adds up.
 */
export function PpaLineItemsPanel({
  expenseClass,
  lines,
  lockedReason,
  showDeleted,
  onToggleDeleted,
  onAdd,
  onEdit,
  onDelete,
  onRestore,
  ceilingApproved,
  ceilingRemaining,
}: {
  /**
   * The PPA's fund source, which every line follows. NULL on a PPA filed before the field
   * existed — and then nothing can be added until it is chosen.
   */
  expenseClass: number | null;
  /** The PPA's lines, in the order the server numbered them. */
  lines: PpaLineItemDto[];
  /**
   * The PPA's own fund-source ceiling and what is left of it — the reference's `Ceiling` band.
   *
   * A POOL, not a per-line figure: the ceiling belongs to the (department, expense class), so
   * it is identical on every row. The
   * reference merges the cell down the whole table for exactly that reason; `<DataTable>`
   * cannot row-span, so the pair repeats per row instead.
   *
   * **NULL is UNPRICED** — the reviewer has not looked — and it renders the em dash. A ceiling
   * of zero is a reviewer approving nothing and renders a muted `0.00`.
   */
  ceilingApproved: number | null;
  ceilingRemaining: number | null;
  lockedReason: string | null;
  showDeleted: boolean;
  onToggleDeleted: () => void;
  onAdd: () => void;
  onEdit: (line: PpaLineItemDto) => void;
  onDelete: (lines: PpaLineItemDto[]) => void;
  onRestore: (line: PpaLineItemDto) => void;
}) {
  /*
    Searched, sorted and paged IN MEMORY. The lines arrive WHOLE with the record, so the
    filter sees every one there is — the 201 file's own exemption, and what would end it is
    paging this server-side.
  */
  const fetchPage = useCallback(
    (query: DataTableQuery) => {
      const term = (query.search ?? '').trim().toLowerCase();

      const matched = term
        ? lines.filter(
            (line) =>
              line.item.toLowerCase().includes(term) ||
              (line.accountCode ?? '').toLowerCase().includes(term) ||
              (line.accountDescription ?? '').toLowerCase().includes(term),
          )
        : lines;

      const field = query.sort?.replace(/^-/, '') ?? 'sortOrder';
      const descending = query.sort?.startsWith('-') ?? false;

      const sorted = [...matched].sort((a, b) => {
        const byField =
          field === 'item'
            ? a.item.localeCompare(b.item)
            : field === 'amount'
              ? a.amount - b.amount
              : a.sortOrder - b.sortOrder;

        return descending ? -byField : byField;
      });

      const start = (query.page - 1) * query.pageSize;

      return Promise.resolve({
        items: sorted.slice(start, start + query.pageSize),
        total: sorted.length,
        page: query.page,
        pageSize: query.pageSize,
      });
    },
    [lines],
  );

  const state = useDataTable<PpaLineItemDto>({
    fetchPage,
    // The order the SERVER numbered them in, so the panel and the record agree about which
    // line is first.
    defaultSort: 'sortOrder',
  });

  const columns = useMemo<DataColumn<PpaLineItemDto>[]>(
    () => [
      {
        /*
          THE ACCOUNT CODE LEADS, and it is STACKED — the description over the code, which is
          the reference's own first column. It read `Item · Account code` with the code alone
          in the mono face, so the account a line is charged to was named nowhere: a column of
          `5-02-03-990` identifies nothing to anyone who has not memorised the chart.

          `<StackedCell secondary>` is refused inside `<IndexTable>` and legal here — the dev
          error is scoped by `IndexTableSurfaceContext`, which a panel does not provide.
        */
        key: 'accountCode',
        header: 'Account Code',
        width: 240,
        render: (row) =>
          row.accountDescription ? (
            <StackedCell primary={row.accountDescription} secondary={row.accountCode ?? ''} />
          ) : (
            // A code is a machine value, so it reads in the mono face like every other one.
            <TextCell value={row.accountCode} mono />
          ),
      },
      {
        key: 'item',
        header: 'Item',
        width: 240,
        sortable: true,
        render: (row) => <TextCell value={row.item} />,
      },
      {
        key: 'startDate',
        header: 'Start',
        width: 130,
        /*
          A CALENDAR date, so it goes through `formatDateOnly` and a `<TextCell mono>` rather
          than `<DateCell>`: that one renders an INSTANT in the viewer's zone, which for a
          reader west of UTC would report a window starting the day before the one recorded.
        */
        render: (row) => (
          <TextCell value={row.startDate ? formatDateOnly(row.startDate) : null} mono />
        ),
      },
      {
        key: 'endDate',
        header: 'End',
        width: 130,
        render: (row) => (
          <TextCell value={row.endDate ? formatDateOnly(row.endDate) : null} mono />
        ),
      },
      {
        key: 'amount',
        header: 'Prop. Amount',
        width: 150,
        align: 'right',
        sortable: true,
        render: (row) => <TextCell value={formatMoney(row.amount)} mono numeric />,
      },
      /*
        THE ADJUSTED AMOUNT IS DRAWN ONLY ONCE THE FUND IS PRICED, which is the reference's own
        `budgetFinalization` gate — its column and its Set-adjusted-amount action appear
        together and only then.

        The per-row answer to that question is whether THIS PPA's fund carries a ceiling: an
        adjustment is a reviewer's verdict against a ceiling, so before one exists the column
        is an em dash on every line and a slot for a figure nobody can yet have set.
      */
      ...(ceilingApproved === null
        ? []
        : [
            {
        /*
          THE REVIEWER'S ADJUSTED AMOUNT, which is the reference's own `ADJ. AMOUNT` column.

          It read `Counted` and rendered the EFFECTIVE amount — the adjustment where there is
          one and the proposal where there is not — which is a derived figure standing in the
          slot the reference uses for a reviewer's own verdict. A reader could not tell an
          untouched line from one adjusted back to its proposed figure.

          An EXCLUDED line stays listed and counts toward nothing — the whole difference
          between excluding a line and deleting one, said in the row rather than left to be
          inferred from a total it is missing from.
        */
              key: 'adjustedAmount',
              header: 'Adj. Amount',
              width: 170,
              align: 'right',
              render: (row) =>
                row.isExcluded ? (
                  <TextCell value="Excluded" />
                ) : (
                  <TextCell value={formatMoney(row.adjustedAmount)} mono numeric />
                ),
            } satisfies DataColumn<PpaLineItemDto>,
          ]),
      {
        /*
          THE CEILING BAND — the reference's own, between the amounts and the row's state.

          Both cells state a FUND-LEVEL figure, so every row under one PPA reads the same pair. The reference merges them down the table; this repeats
          them, which is the one shape difference in the band.

          NO FOOTER on either, and that is not an omission: the totals row under this table
          sums a column of per-line figures, and a ceiling repeated on twelve rows would total
          to twelve times itself.
        */
        key: 'ceilingApproved',
        header: 'Approved',
        group: 'Ceiling',
        width: 150,
        align: 'right',
        render: () => (
          <TextCell
            value={ceilingApproved === null ? null : formatMoney(ceilingApproved)}
            mono
            numeric
          />
        ),
      },
      {
        key: 'ceilingRemaining',
        header: 'Remaining',
        group: 'Ceiling',
        width: 150,
        align: 'right',
        // NEGATIVE where the fund is over-proposed, and nothing clamps it — that overrun is
        // what the band is read for.
        render: () => (
          <TextCell
            value={ceilingRemaining === null ? null : formatMoney(ceilingRemaining)}
            mono
            numeric
          />
        ),
      },
      {
        key: 'status',
        header: 'Status',
        width: 110,
        // The panel can show deleted lines, so it owes the marker: the one state a row must
        // never be silent about.
        status: true,
        render: (row) => (
          <StatusText tone={row.isActive ? 'active' : 'inactive'}>
            {row.isActive ? 'Active' : 'Deleted'}
          </StatusText>
        ),
      },
    ],
    [ceilingApproved, ceilingRemaining],
  );

  const classLabel = expenseClass === null ? null : (PPA_EXPENSE_CLASS_LABEL[expenseClass] ?? null);

  /** The PPA's total — live, unexcluded lines, at the figure that COUNTS. */
  const total = lines
    .filter((line) => line.isActive && !line.isExcluded)
    .reduce((sum, line) => sum + line.effectiveAmount, 0);

  return (
    <div className="flex flex-col gap-4">
      <DataTablePanel<PpaLineItemDto>
        id="ppa-lines"
        state={state}
        columns={columns}
        rowKey={(row) => row.id}
        searchPlaceholder="Search items, account codes…"
        emptyMessage={
          classLabel
            ? `Nothing is budgeted under ${classLabel} yet.`
            : 'Nothing is budgeted yet. Choose this PPA\u2019s fund source first.'
        }
        emptySearchMessage="No line matches that search."
        /*
          NO Other Info and NO creation columns. A budgeted line carries no provenance on the
          wire — the PPA's own trail is on its Info tab and in History Logs — so the popover
          would be four em dashes, which is the dead affordance the hide rule forbids.
        */
        otherInfo={() => null}
        auditColumns={false}
        settingsItems={[
          {
            key: 'show-deleted',
            label: 'Show deleted',
            checked: showDeleted,
            onClick: onToggleDeleted,
            section: 'Records',
          },
        ]}
        bulkActions={(rows) => [
          {
            key: 'bulk-delete',
            label: `Delete ${rows.length} line${rows.length === 1 ? '' : 's'}`,
            permission: 'ppas.update',
            destructive: true,
            disabledReason:
              lockedReason ??
              (rows.every((row) => row.isActive) ? null : 'Some of these are already deleted.'),
            onClick: () => onDelete(rows),
          },
        ]}
        /*
          The STANDARD create — the panel draws it. `Add lines` rather than a bare `Create`,
          because a line is what this tab holds and the plural is what the grid collects.
        */
        onCreate={onAdd}
        createLabel="Add lines"
        createPermission="ppas.update"
        createDisabledReason={
          lockedReason ??
          (classLabel === null
            ? 'This PPA has no fund source yet. Edit the PPA on its Info tab and choose one first.'
            : null)
        }
        onEdit={(row) => onEdit(row)}
        editPermission="ppas.update"
        onDelete={(row) => onDelete([row])}
        deletePermission="ppas.update"
        deleteDisabledReason={(row) =>
          row.isActive ? lockedReason : 'This line is already deleted.'
        }
        rowActions={(row) =>
          row.isActive
            ? []
            : [
                {
                  key: `restore-${row.id}`,
                  label: 'Restore',
                  icon: <RotateCcw className="h-4 w-4" aria-hidden="true" />,
                  permission: 'ppas.update',
                  disabledReason: lockedReason,
                  onClick: () => onRestore(row),
                },
              ]
        }
      />

      <PreviewTable
        variant="table-display"
        rows={[
          {
            label: classLabel ? `${classLabel} total` : 'Total',
            value: formatMoney(total),
            mono: true,
          },
        ]}
      />

      {classLabel ? (
        <p className="text-xs text-slate-500">
          Every line here is filed under {classLabel}, this PPA&rsquo;s fund source. A line under
          another class belongs on a PPA filed under that class.
        </p>
      ) : null}
    </div>
  );
}
